import {
  OnInit,
  Inject,
  OnChanges,
  DestroyRef,
  Directive,
  ElementRef,
  SimpleChanges,
  AfterViewInit,
  DOCUMENT,
  contentChild,
  Signal,
  input,
  InputSignal,
  computed
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[appBlockUi]',
  host: {
    '[class.overflow-hidden]': 'markSelfAsContainerPosition()',
    '[class.position-relative]': 'markSelfAsContainerPosition()',
  }
})
export class BlockUiDirective implements OnChanges, OnInit, AfterViewInit {

  /**
   * @private _elementBlock
   * @description The element to display when blocking is active.
   * Either user-provided via `#blockElement`, or created dynamically.
   */
  private _elementBlock!: HTMLElement;

  /**
   * @public show$
   * @description Observable that emits the blocking state.
   */
  public readonly show$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * @contentChild blockElementRef
   * @description Projected element reference to display as block UI.
   */
  public blockElementRef: Signal<ElementRef<HTMLElement> | undefined> = contentChild<ElementRef<HTMLElement>>('blockElement')

  /**
   * @input appBlockUi
   * @description Controls whether the UI should be blocked.
   * Adds/removes `.show` class on the overlay.
   * @required
   */
  public readonly isBlock: InputSignal<boolean> = input.required({
    alias: 'appBlockUi',
    transform: (v) => Boolean(v)
  });

  /**'
   * @description skip set css props: position-relative & overview-hide at the host
   */
  public readonly skipSelfPositionValue: InputSignal<boolean> = input(false, {
    transform: (v) => Boolean(v)
  });

  /**
   * @description to set css props or not depend on on block status and skipSelf
   */
  public readonly markSelfAsContainerPosition: Signal<boolean> = computed(() => {
    return this.isBlock() && !this.skipSelfPositionValue()
  })

  /**
   * @param _element 
   */
  constructor(
    private _destroyRef: DestroyRef,
    private _element: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private _document: Document
  ) {}

  /**
   * @lifecycle ngOnChanges
   * @description Emits block state changes to the show$ observable.
   */
  ngOnChanges(changes: SimpleChanges): void {
    
    if ('isBlock' in changes) {
      this.show$.next(this.isBlock());
    }

  }

  /**
   * @lifecycle ngOnInit
   * @description Assigns projected element reference to internal variable.
   */  
  ngOnInit(): void {}

  /**
   * @lifecycle ngAfterViewInit
   * @description Subscribes to block state changes and manages overlay display.
   */
  ngAfterViewInit(): void {

    const blockElement = this.blockElementRef()

    // replace element block
    if (blockElement != null) this._elementBlock = blockElement.nativeElement;

    // create with instance
    this._createBlockElement();
    
    // watch show status
    this.show$
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe(value => {
            value ? this._showBlockElement() : this._hideBlockElement();
          })

  }

  /**
   * @method _createBlockElement
   * @description Creates a default block element if one is not projected.
   */
  private _createBlockElement(): void {
    
    // check if element is created before
    if (this._elementBlock == null) {
      // create element
      this._elementBlock = this._document.createElement('span');
    }
    

    this._elementBlock.id = "ngBlockUi";
    this._elementBlock.classList.add('ng_block_ui');

    this._element.nativeElement.appendChild(this._elementBlock);

  }

  /**
   * @method _showBlockElement
   * @description Adds `.show` class to block element.
   */
  private _showBlockElement(): void {

    // add show class
    if (this._elementBlock != null) {
      this._elementBlock.classList.add('show')
    }

  }

  /**
   * @method _hideBlockElement
   * @description Removes `.show` class from block element.
   */
  private _hideBlockElement(): void {
    
    // remove element
    if (this._elementBlock != null) {
      this._elementBlock.classList.remove('show')
    }

  }

}
